import React from 'react';
import { formatCurrency } from '@smartphone-tycoon/shared-ui';
import { calculateMonthlyRevenue } from '../../utils/businessCalculations';
import type { Product } from '@smartphone-tycoon/shared-core';

interface BusinessMetricsProps {
  companyState: {
    cash: number;
    reputation: number;
    marketShare: number;
    day: number;
    month: number;
    year: number;
  };
  products?: Product[];
}

// Helper function to safely format numeric values
const formatPercentage = (value: number, defaultValue = 'N/A') => {
  if (isNaN(value) || !isFinite(value)) {
    return defaultValue;
  }
  return `${value}%`;
};

export function BusinessMetrics({ companyState, products = [] }: BusinessMetricsProps) {
  // Filter out discontinued products
  const activeProducts = products.filter(p => p.status === 'production');
  
  // Calculate metrics only if there are active products
  const hasActiveProducts = activeProducts.length > 0;
  const monthlyRevenue = hasActiveProducts ? calculateMonthlyRevenue(companyState.marketShare, companyState.reputation) : 0;
  const monthlySales = hasActiveProducts ? Math.round(50000 * companyState.marketShare) : 0;
  // const _growthRate = hasActiveProducts ? Math.round((companyState.marketShare * 100 - 10) * 2) / 2 : 0;

  // Calculate expense categories based on revenue
  const expenses = hasActiveProducts ? [
    {
      id: 1,
      category: 'Manufacturing',
      description: 'Production costs and materials',
      amount: Math.round(monthlyRevenue * 0.45),
      percentage: 45
    },
    {
      id: 2,
      category: 'R&D',
      description: 'Research and development',
      amount: Math.round(monthlyRevenue * 0.18),
      percentage: 18
    },
    {
      id: 3,
      category: 'Marketing',
      description: 'Advertising and promotions',
      amount: Math.round(monthlyRevenue * 0.13),
      percentage: 13
    },
    {
      id: 4,
      category: 'Operations',
      description: 'General operations and overhead',
      amount: Math.round(monthlyRevenue * 0.24),
      percentage: 24
    }
  ] : [];

  // Calculate revenue breakdown
  const revenueBreakdown = {
    productSales: activeProducts.reduce((sum, product) => {
      const productSales = Math.round(monthlySales * (product.marketAppeal / 100));
      return sum + (productSales * product.price);
    }, 0)
  };

  const totalRevenue = Object.values(revenueBreakdown).reduce((a, b) => a + b, 0);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-theme-bg-content rounded-lg shadow p-4">
          <h3 className="text-lg font-semibold text-theme-text-primary mb-4">Revenue Breakdown</h3>
          {hasActiveProducts ? (
            <div className="space-y-4">
              <div className="flex justify-between p-3 bg-theme-bg-secondary rounded-lg">
                <div>
                  <h4 className="font-medium text-theme-text-primary">Product Sales</h4>
                  <p className="text-sm text-theme-text-muted">Direct device sales revenue</p>
                </div>
                <div className="text-right">
                  <p className="font-medium text-theme-success">{formatCurrency(revenueBreakdown.productSales)}</p>
                  <p className="text-sm text-theme-text-muted">
                    {formatPercentage(Math.round((revenueBreakdown.productSales / totalRevenue) * 100))} of total
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-theme-text-muted">
              No active products to generate revenue
            </div>
          )}
        </div>

        <div className="bg-theme-bg-content rounded-lg shadow p-4">
          <h3 className="text-lg font-semibold text-theme-text-primary mb-4">Expenses Breakdown</h3>
          {hasActiveProducts ? (
            <div className="space-y-4">
              {expenses.map((expense) => (
                <div key={expense.id} className="flex justify-between p-3 bg-theme-bg-secondary rounded-lg">
                  <div>
                    <h4 className="font-medium text-theme-text-primary">{expense.category}</h4>
                    <p className="text-sm text-theme-text-muted">{expense.description}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-medium text-theme-error">-{formatCurrency(expense.amount)}</p>
                    <p className="text-sm text-theme-text-muted">{formatPercentage(expense.percentage)} of total</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-theme-text-muted">
              No active products to calculate expenses
            </div>
          )}
        </div>

      </div>
    </div>
  );
}